import {
  DropdownRoot,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
  DropdownSeparator,
} from "@/components/ui/DropdownMenu";
import { useAppState, useSelectAccount } from "@/lib/queries";
import { useToast } from "@/components/ui/Toast";

interface TenancyAccount {
  id: string;
  kind?: string;
  account?: { companyName?: string };
}

interface AppShape {
  tenancy?: {
    selectedAccountId?: string;
    accounts?: TenancyAccount[];
  };
  uiAccess?: {
    isPlatformMember?: boolean;
    viewAs?: { active?: boolean };
  };
}

/**
 * Tenant picker rendered into the topbar.
 *
 *   - Non-platform users: shows the dropdown only if they have access to
 *     more than one tenant. Standard switch — sets rros_account.
 *   - Platform members: always shown. Picking a non-platform tenant
 *     triggers view-as via /api/accounts/select; the ViewAsBanner then
 *     lights up on the operator surface.
 *
 * Visually subtle (chip in the topbar), so it stays out of the way for
 * the 99% of single-tenant operators.
 */
export function AccountSwitcher() {
  const { data: appRaw } = useAppState();
  const selectMut = useSelectAccount();
  const { toast } = useToast();

  const app = (appRaw ?? {}) as AppShape;
  const accounts = app.tenancy?.accounts ?? [];
  const selectedId = app.tenancy?.selectedAccountId ?? "";
  const isPlatformMember = Boolean(app.uiAccess?.isPlatformMember);

  // Single-tenant operator: don't render the chip at all — there's
  // nowhere to switch to.
  if (!isPlatformMember && accounts.length <= 1) return null;

  const selectedName =
    accounts.find((entry) => entry.id === selectedId)?.account?.companyName ||
    (isPlatformMember ? "Platform" : selectedId || "—");

  const handleSelect = (accountId: string) => {
    if (accountId === selectedId) return;
    selectMut.mutate(
      { accountId },
      {
        onError: (err) =>
          toast({
            title: "Couldn't switch tenant",
            description: (err as Error).message,
            tone: "error",
          }),
      },
    );
  };

  return (
    <DropdownRoot>
      <DropdownTrigger asChild>
        <button
          type="button"
          aria-label="Switch tenant"
          className="hidden md:flex items-center gap-1 px-2 py-1 rounded-dw-sm text-xs text-dw-text-2 hover:text-dw-text hover:bg-dw-surface-h transition-colors max-w-[200px]"
        >
          <span className="truncate">{selectedName}</span>
          <svg
            className="h-3 w-3 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </DropdownTrigger>
      <DropdownContent align="end">
        <div className="px-dw-3 py-dw-2 text-xs text-dw-text-3">
          {isPlatformMember
            ? "Pick a tenant to view as — your actions are audited."
            : "Switch active workspace"}
        </div>
        <DropdownSeparator />
        {accounts.length === 0 ? (
          <div className="px-dw-3 py-dw-2 text-xs text-dw-text-3">
            No tenants available.
          </div>
        ) : (
          accounts.map((entry) => (
            <DropdownItem
              key={entry.id}
              onSelect={() => handleSelect(entry.id)}
              disabled={selectMut.isPending}
            >
              <span className="truncate">
                {entry.account?.companyName || entry.id}
              </span>
              {entry.id === selectedId && (
                <span className="ml-auto text-xs text-dw-text-3">current</span>
              )}
            </DropdownItem>
          ))
        )}
      </DropdownContent>
    </DropdownRoot>
  );
}
